import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../providers/AuthContext"; // Import the AuthContext hook
import UserActionsDropdown from "./UserActionsDropdown";
import BalanceDropDown from "./BalanceDropDown";
import { HiMiniBars3 } from "react-icons/hi2";
import Logo from "../../../assets/images/logo.svg";
import { isEmpty } from "../../../helpers/functions";
import Login from "../../../pages/Auth/LoginPage";
import { FaBars } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import MobileNavigation from "../MobileNavigation";
import { UserLanguageDropdown } from "./UserLanguageDropdown";
import { useSportsbook } from "../../../providers/SportsbookContext";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { cn } from "../../../helpers/utils";

const AppHeader = ({ onLoginClick, onRegisterClick, isSidebarOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { sendMessageToSportsbook } = useSportsbook();
  const { user, logout } = useAuth(); // Access user and logout from AuthContext
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef(null);
  const itemsRef = useRef([]);
  const initialMeasurementDone = useRef(false);

  const isUserRole = user?.role === "User";

  const [isMobile, setIsMobile] = useState(window?.innerWidth <= 991);

  // Define APP_NAV here, before it's used in useEffect
  const APP_NAV = useMemo(
    () => [
      ...((user && user.role === "User") || isEmpty(user)
        ? [
            {
              path: "/sportsbook/sports",
              label: "SPORTS BETTING",
              id: "sports",
            },
            { path: "/sportsbook/live", label: "LIVE", id: "live" },
          ]
        : []),

      { path: "/livecasino", label: "LIVE CASINO", id: "livecasino" },
      { path: "/casino/slots", label: "CASINO", id: "casino" },
      // { path: "/live-games/57587", label: "LIVE GAMES", id: "games" },
      { path: "/spaceman/44597", label: "SPACEMAN", id: "spaceman" },
      { path: "/virtuals", label: "VIRTUALS", id: "virtuals" },
      // { path: "/promotions", label: "PROMOTION", id: "promotions" },
    ],
    [user]
  );

  // Initialize with all navigation items visible
  const [visibleItems, setVisibleItems] = useState(APP_NAV);
  const [overflowItems, setOverflowItems] = useState([]);

  useEffect(() => {
    itemsRef.current = Array(APP_NAV.length)
      .fill()
      .map((_, i) => itemsRef.current[i] || React.createRef());
  }, [APP_NAV.length]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 991;
      setIsMobile(newIsMobile);

      if (!newIsMobile) {
        window.requestAnimationFrame(() => {
          calculateVisibleItems(true);
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer); // Cleanup the timer on component unmount
  }, []);

  useLayoutEffect(() => {
    if (isMobile) {
      setVisibleItems(APP_NAV);
      setOverflowItems([]);
      return;
    }

    const timer = setTimeout(() => {
      window.requestAnimationFrame(() => {
        calculateVisibleItems();
        initialMeasurementDone.current = true;
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [APP_NAV, isMobile, user]);

  useEffect(() => {
    if (initialMeasurementDone.current && !isMobile) {
      window.requestAnimationFrame(() => {
        calculateVisibleItems();
      });
    }
  }, [user, isMobile, APP_NAV]); // Add isMobile and APP_NAV to dependencies

  useEffect(() => {
    if (!navRef.current || isMobile) return;

    // Create a ResizeObserver to detect container width changes
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        calculateVisibleItems(true);
      });
    });

    // Start observing the nav container
    resizeObserver.observe(navRef.current);

    // Force an initial calculation after a delay to ensure fonts are loaded
    const initialCalcTimer = setTimeout(() => {
      window.requestAnimationFrame(() => {
        calculateVisibleItems(true);
        initialMeasurementDone.current = true;
      });
    }, 500); // Wait for fonts to load

    return () => {
      resizeObserver.disconnect();
      clearTimeout(initialCalcTimer);
    };
  }, [navRef.current, isMobile, APP_NAV]);

  const formattedDate = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Tunis",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(currentTime);

  const formattedTime = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Tunis",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(currentTime);

  const calculateVisibleItems = (forceRecalculate = false) => {
    if (!navRef.current || isMobile) {
      // Only update state if it's different from current state to prevent infinite loops
      if (JSON.stringify(visibleItems) !== JSON.stringify(APP_NAV) || overflowItems.length > 0) {
        setVisibleItems(APP_NAV);
        setOverflowItems([]);
      }
      return;
    }

    const navWidth = navRef.current.offsetWidth;
    const moreButtonWidth = 50; // Reserve space for "More" button
    const availableWidth = navWidth - moreButtonWidth;

    // Always recalculate on initial load
    if (!initialMeasurementDone.current) {
      forceRecalculate = true;
    }

    // Skip unnecessary calculations
    if (!forceRecalculate && overflowItems.length === 0 && visibleItems.length === APP_NAV.length) {
      return;
    }

    let totalWidth = 0;
    const visible = [];
    const overflow = [];

    APP_NAV.forEach((item, index) => {
      const itemNode = itemsRef.current[index]?.current;
      let itemWidth = 0;

      if (itemNode) {
        // Use getBoundingClientRect for more accurate measurements
        itemWidth = itemNode.getBoundingClientRect().width;
      } else {
        // Fallback estimation if ref not available yet
        itemWidth = item.label.length * 12 + 32;
      }

      if (totalWidth + itemWidth <= availableWidth) {
        totalWidth += itemWidth;
        visible.push(item);
      } else {
        overflow.push(item);
      }
    });

    // Only update state if there's a change to avoid re-renders
    if (
      JSON.stringify(visible.map((i) => i.id)) !== JSON.stringify(visibleItems.map((i) => i.id)) ||
      JSON.stringify(overflow.map((i) => i.id)) !== JSON.stringify(overflowItems.map((i) => i.id))
    ) {
      setVisibleItems(visible);
      setOverflowItems(overflow);
    }
  };

  // Navigation item component with ref for measuring
  const NavigationItem = ({ item, index, inDropdown = false, close = null }) => (
    <NavLink
      key={item.id}
      to={item.path}
      ref={!inDropdown ? itemsRef.current[index] : null}
      className={`px-3 xl:text-base text-sm leading-10 font-bold text-nowrap ${
        inDropdown
          ? "block w-full text-left hover:bg-[#44485c] hover:text-primary text-white py-2 pr-5"
          : "[&.active]:text-primary [&.active]:border-b-2 border-primary text-white hover:text-primary hover:border-b-2"
      }`}
      onClick={() => {
        if (item.path === "/sportsbook/sports") {
          sendMessageToSportsbook({ changePage: "sport" });
        } else if (item.path === "/sportsbook/live") {
          sendMessageToSportsbook({ changePage: "live" });
        }
        // if (close) {
        //   close();
        // }
      }}
    >
      {item.label}
    </NavLink>
  );

  return (
    <>
      <MobileNavigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <header className="relative z-10 text-white" id="appHeader" style={{ boxShadow: "inset 0 -1px 0 0 #67889f29" }}>
        <div className="flex items-center justify-between py-2 px-3">
          <div className="flex">
            {/* Left Section */}
            <div className="block lg:hidden bg-[#44485c] px-3 rounded-xl mr-3">
              <button
                className="flex flex-col items-center gap-1 py-2 font-light text-white hover:text-primary/70 hover:bg-[#1c1c1c]"
                onClick={() => setIsMenuOpen(true)}
              >
                <FaBars size={20} />
              </button>
            </div>
            <Link to={isUserRole ? "/" : "/admin/transfer"} className="flex items-center space-x-4">
              <span className="flex mr-8 lg:max-w-60 min-w-24 xl:h-12 h-10">
                <img src={Logo} alt="logo" title="logo" />
              </span>
            </Link>
          </div>

          {/* Center Section (Desktop) */}
          {isUserRole || !user ? (
            <nav className="hidden lg:flex items-center justify-center overflow-hidden flex-1" ref={navRef}>
              {visibleItems.map((item, index) => (
                <NavigationItem key={item.id} item={item} index={index} />
              ))}

              {/* More dropdown for overflow items */}
              {overflowItems.length > 0 && (
                <Menu as="div" className={cn("relative inline-block text-left z-10 ml-4")}>
                  {({ open }) => (
                    <>
                      <MenuButton>
                        <BsThreeDots size={20} />
                      </MenuButton>

                      <MenuItems
                        anchor="bottom end"
                        as="div"
                        className={cn("z-10 px-2 mt-4 origin-top-right rounded-md bg-primary-dark shadow-lg focus:outline-none")}
                      >
                        {({ close }) => (
                          <>
                            {overflowItems.map((item, index) => (
                              <MenuItem key={index}>
                                {() => (
                                  <NavigationItem key={item.id} item={item} index={visibleItems.length + index} inDropdown={true} close={close} />
                                )}
                              </MenuItem>
                            ))}
                          </>
                        )}
                      </MenuItems>
                    </>
                  )}
                </Menu>
              )}
            </nav>
          ) : null}

          {/* Right Section */}
          <div className="flex items-center ">
            {user ? (
              <div className="bg-[#44485c] rounded-xl flex items-center">
                <div className="md:block hidden">
                  <BalanceDropDown user={user} />
                </div>
                <UserActionsDropdown user={user} logout={logout} />
              </div>
            ) : (
              <>
                <Login />
                <div className="block md:hidden">
                  <button
                    onClick={() => {
                      onLoginClick();
                    }}
                    className="bg-[#575c75] w-full font-medium px-3 py-2 rounded-lg leading-3 text-sm transition duration-300 h-9  text-white disabled:opacity-50 hover:bg-[#575c75]/80"
                  >
                    {t("LOGIN")}
                  </button>
                </div>
              </>
            )}
            <div className="ml-2">
              <UserLanguageDropdown />
            </div>
          </div>
        </div>
      </header>
      {isMobile && user && !isUserRole && location.pathname.startsWith("/admin") && (
        <div className="p-5">
          <div className="flex justify-between">
            <div className="flex flex-col justify-start float-start gap-1">
              <p className="text-lg font-semibold">{t("AGENT MENU")}</p>
              <p className="text-sm flex-row space-x-1 rtl:space-x-reverse opacity-70">{`${formattedDate} - ${formattedTime} Africa/Tunis`}</p>
            </div>

            <button onClick={() => toggleSidebar(!isSidebarOpen)} className="text-white focus:outline-none mr-3">
              {isSidebarOpen ? <HiMiniBars3 size={40} /> : <HiMiniBars3 size={40} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppHeader;
